import { Card, Divider, Pagination, Table, Typography, message, Button, Modal, Form, Input, Popconfirm } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import baseuri from '../../methods/baseuri';
import { useAppDispatch, useAppSelector } from '../../methods/redux/hooks';
import { retrieveAdminBlacklistedWallets } from '../../methods/redux/reducers/admin-blacklisted-wallets';
import { hideEmail } from '../../methods/utils/request-helpers';

const { Title, Text } = Typography;

const PageWrapper = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
	gap: 1em;

	& .myCard {
		border-radius: 10px;
		gap: 1em;
	}
`;

const HoverableAddress = styled(Text)`
	color: #060709ff !important;
	transition: color 0.3s;
	&:hover {
		color: #40a9ff !important;
		cursor: pointer;
	}
`;

const BlacklistedWallets = () => {
	const dispatch = useAppDispatch();
	const [searchParams, setSearchParams] = useSearchParams();
	const currentPage = Number(searchParams.get('pageId')) || 1;
	const pageSize = Number(searchParams.get('perPage')) || 20;

	const { blacklistedWallets, loading } = useAppSelector(
		(store: any) => store.adminBlacklistedWallets
	);

	const [visible, setVisible] = useState(false);
	const [form] = Form.useForm();
	const [submitting, setSubmitting] = useState(false);

	const handleCreate = async () => {
		try {
			const values = await form.validateFields();
			setSubmitting(true);
			await baseuri.post('/admin/blacklist-wallets/', values);
			message.success('Wallet blacklisted successfully');
			setVisible(false);
			form.resetFields();
			dispatch(
				retrieveAdminBlacklistedWallets({
					pageId: currentPage,
					perPage: pageSize,
					sort: 'desc',
				})
			);
		} catch (error: any) {
			if (error.response) {
				message.error(error.response.data.message || 'Error creating blacklisted wallet');
			}
		} finally {
			setSubmitting(false);
		}
	};

	const handleDelete = async (id: string) => {
		try {
			await baseuri.delete(`/admin/blacklist-wallets?id=${id}`);
			message.success('Blacklisted wallet deleted successfully');
			dispatch(
				retrieveAdminBlacklistedWallets({
					pageId: currentPage,
					perPage: pageSize,
					sort: 'desc',
				})
			);
		} catch (error: any) {
			message.error(error.response?.data?.message || 'Failed to delete blacklisted wallet');
		}
	};

	useEffect(() => {
		dispatch(
			retrieveAdminBlacklistedWallets({
				pageId: currentPage,
				perPage: pageSize,
				sort: 'desc',
			})
		);
	}, [searchParams, dispatch]);

	const columns = [
		{
			title: 'S/N',
			dataIndex: 'number',
			key: 'number',
			render: (_: any, __: any, index: number) => (
				<strong>{(currentPage - 1) * pageSize + index + 1}</strong>
			),
		},
		{
			title: 'Full Name',
			dataIndex: 'userId',
			key: 'userId_name',
			render: (userId: any) => <Text strong>{userId?.fullName || 'N/A'}</Text>,
		},
		{
			title: 'Username',
			dataIndex: 'userId',
			key: 'userId_uname',
			render: (userId: any) => <Text strong>{userId?.username || 'N/A'}</Text>,
		},
		{
			title: 'User Email',
			dataIndex: 'userEmail',
			key: 'userEmail',
			render: (userEmail: any, record: any) => (
				<Link to={`/user-profile/${record.userId?._id}`}>
					<Text strong>{hideEmail(userEmail)}</Text>
				</Link>
			),
		},
		{
			title: 'Wallet Address',
			dataIndex: 'walletAddress',
			key: 'walletAddress',
			render: (walletAddress: any) => (
				<HoverableAddress strong>{walletAddress}</HoverableAddress>
			),
		},
		{
			title: 'Comment',
			dataIndex: 'comment',
			key: 'comment',
			render: (comment: string) => <Text>{comment}</Text>,
		},
		{
			title: 'Created At',
			dataIndex: 'createdAt',
			key: 'createdAt',
			render: (createdAt: string) => (
				<Text>{moment(createdAt).format('MMM DD, YY / hh:mm:ss a')}</Text>
			),
		},
		{
			title: 'Action',
			key: 'action',
			render: (record: any) => (
				<div style={{ display: 'flex', gap: '8px' }}>
					<Link to={`/wallets/blacklisted-wallets/${record._id}`}>
						<Button type="primary" size="small">View</Button>
					</Link>
					<Popconfirm
						title="Are you sure you want to delete this entry?"
						onConfirm={() => handleDelete(record._id)}
						okText="Yes"
						cancelText="No"
					>
						<Button danger size="small">Delete</Button>
					</Popconfirm>
				</div>
			),
		},
	];

	return (
		<PageWrapper>
			<Card className="myCard">
				<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<div>
						<Title level={3}>Blacklisted Wallets</Title>
						<Text>This page displays the list of blacklisted wallets.</Text>
					</div>
					<Button type="primary" icon={<PlusOutlined />} onClick={() => setVisible(true)}>
						Create Blacklist Wallet
					</Button>
				</div>
				
				<Divider />

				<Table
					loading={loading}
					dataSource={blacklistedWallets?.itemsList || []}
					columns={columns}
					rowKey="_id"
					pagination={false}
					scroll={{ x: 'max-content' }}
				/>

				<Divider />

				<Pagination
					onChange={(page, pageSize) => {
						setSearchParams({ pageId: String(page), perPage: String(pageSize) });
					}}
					total={blacklistedWallets?.paginator?.itemCount || 0}
					current={currentPage}
					pageSize={pageSize}
					showSizeChanger
					showQuickJumper
				/>

				<Modal
					title="Blacklist a Wallet"
					visible={visible}
					onCancel={() => setVisible(false)}
					onOk={handleCreate}
					confirmLoading={submitting}
				>
					<Form form={form} layout="vertical">
						<Form.Item
							name="userEmail"
							label="User Email"
							rules={[{ required: true, message: 'Please input user email!', type: 'email' }]}
						>
							<Input placeholder="Enter user's email address" />
						</Form.Item>
						<Form.Item
							name="walletAddress"
							label="Wallet Address"
							rules={[{ required: true, message: 'Please input wallet address!' }]}
						>
							<Input placeholder="Enter wallet address to blacklist" />
						</Form.Item>
						<Form.Item
							name="comment"
							label="Comment"
							rules={[{ required: true, message: 'Please input a reason/comment!' }]}
						>
							<Input.TextArea placeholder="Reason for blacklisting" />
						</Form.Item>
					</Form>
				</Modal>
			</Card>
		</PageWrapper>
	);
};

export default BlacklistedWallets;
